"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
var _typeof = require("@babel/runtime/helpers/typeof");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.GontijoCheckoutLoginButton = GontijoCheckoutLoginButton;
exports.useGontijoCheckoutLoginNeeded = useGontijoCheckoutLoginNeeded;
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _react = _interopRequireWildcard(require("react"));
var _reactI18next = require("react-i18next");
var _MainButton = _interopRequireDefault(require("@/ui/atoms/MainButton"));
var _useGontijoSession2 = _interopRequireDefault(require("@/hooks/useGontijoSession"));
var _webview = require("@/utils/webview");
var _widgetBridge = require("@/services/gontijo-session/widgetBridge");
var _GontijoLoginWidget = _interopRequireDefault(require("./GontijoLoginWidget"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != _typeof(e) && "function" != typeof e) return { "default": e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n["default"] = e, t && t.set(e, n), n; }
/** Its own mount id: the header renders the same widget, and two cannot share a host. */
var WIDGET_HOST_ID = 'gontijo-login-widget-checkout';

/** Remounts one press may spend on a widget that failed to mount, before the error shows. */
var MAX_MOUNT_RETRIES = 2;
/**
 * Whether the pay button should ask for a sign-in instead of launching a payment.
 *
 * The same condition the removed gate rendered on, moved to the CTA. Deliberately stricter than
 * `selectGontijoPaymentBlocked`, which answers true before storage has been read: a buyer who is
 * signed in must never be shown a login CTA for the tick it takes to hydrate. Until then the
 * ordinary button renders, disabled by that selector — brief, and truthful either way.
 *
 * Exchange keeps the disabled button and gets no CTA, matching what the gate did. Those buyers
 * arrive from Mis Viajes with a session, so this only fires if theirs expired mid-flow, and the
 * exchange surface has never offered a login.
 *
 * Nor in the native app's WebView: the app owns that session and stamps `client_auth` on the
 * purchase server-side, so the page there gets the ordinary pay button, not a web login.
 * @param {object} [options] - Options.
 * @param {boolean} [options.isExchange] - Whether this checkout is an exchange.
 * @returns {boolean} True when the CTA must be a sign-in.
 */
function useGontijoCheckoutLoginNeeded() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$isExchange = _ref.isExchange,
    isExchange = _ref$isExchange === void 0 ? false : _ref$isExchange;
  var _useGontijoSession = (0, _useGontijoSession2["default"])(),
    enabled = _useGontijoSession.enabled,
    isAuthenticated = _useGontijoSession.isAuthenticated,
    isHydrated = _useGontijoSession.isHydrated;
  return Boolean(enabled) && !isExchange && !(0, _webview.isNativeAppWebView)() && Boolean(isHydrated) && !isAuthenticated;
}

/**
 * GontijoCheckoutLoginButton component. Renders the checkout CTA for a Gontijo buyer with no
 * session: the pay button, reading "sign in".
 *
 * Replaces the standalone gate that used to sit above the payment options. Gontijo sells to
 * signed-in buyers only — the backend reads `client_auth` off the ticket and the payment and issues
 * neither without it — so the sign-in is not a panel beside the purchase, it *is* the next step of
 * it. This is the shape saas-app already ships (`PaymentComponent.renderPaymentButton`): one CTA,
 * same button, the label and the action swapped until the buyer is in.
 *
 * The login itself is still the shared widget, the same one the header mounts, so there is no
 * second login screen anywhere in the funnel. It is mounted here in a hidden host and opened by
 * clicking its own trigger, because `siempreplus-widget` is a preact-habitat mount that exposes no
 * imperative open.
 *
 * That host is `absolute`, and the reason is layout, not looks. `CheckoutNavigation` lays its
 * children out with `justify-content: space-between`, so an in-flow host becomes the last item of
 * that row and shoves the CTA in from the right edge, where every other checkout puts it. Taking
 * the host out of flow leaves the row with exactly the children it had before this component
 * existed. It is not `sr-only` either: the widget's modal is `position: fixed` but renders *inside*
 * the host, and `sr-only`'s `clip` would take the modal down with the trigger, while neither
 * `overflow` nor an absolutely positioned ancestor clips a fixed descendant.
 *
 * While the host is hidden its trigger gets `tabindex="-1"` and `aria-hidden`, so keyboard and
 * screen-reader users do not land on a zero-size button; the CTA is their way in. Those go on the
 * trigger, not the host: the modal renders inside the host and must stay reachable once open. The
 * widget mounts a tick late and redraws the trigger on sign-out, hence the observer.
 *
 * Everything that leans on the widget's internals — the trigger's selector, the sign-out topic, the
 * observer — lives in `widgetBridge`, so a widget refactor has one file to break.
 *
 * This host mounts the widget with `startupOnMount` off: the CTA only renders when the funnel has no
 * session, so a cookie restore here would be a `validate_token` that buys nothing. The widget can
 * still greet a user, though: it adopts a session another instance left on the pub/sub, and keeps
 * its own copy after that, which neither the funnel's clock expiry nor `signOutGontijoSession`
 * reaches. Since the funnel says there is no session, such a widget is stale: the click tells it to
 * sign out and looks for the trigger again on the next frame. Only if the trigger is still missing —
 * the widget's markup changed — is the host put back in flow and revealed, so the buyer still has a
 * way in rather than a CTA that does nothing, and the bridge reports it.
 *
 * A widget that failed to mount is different: its host is empty, so revealing it offers nothing.
 * The press remounts it instead, up to `MAX_MOUNT_RETRIES` times, and opens the sign-in once a
 * mount lands. If none does, an inline error with a retry takes the host's place; each retry, or a
 * new press of the CTA, is a fresh bounded round.
 * @param {object} props - Component props.
 * @param {boolean} [props.isLoading] - Whether the checkout is busy; mirrors the pay button.
 * @returns {React.ReactElement} The sign-in CTA.
 */
function GontijoCheckoutLoginButton(_ref2) {
  var _ref2$isLoading = _ref2.isLoading,
    isLoading = _ref2$isLoading === void 0 ? false : _ref2$isLoading;
  var _useTranslation = (0, _reactI18next.useTranslation)('payment'),
    t = _useTranslation.t;
  var hostRef = (0, _react.useRef)(null);
  var _useState = (0, _react.useState)(false),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    revealed = _useState2[0],
    setRevealed = _useState2[1];
  var cancelRetryRef = (0, _react.useRef)(null);
  (0, _react.useEffect)(function () {
    var host = hostRef.current;
    if (!host) return undefined;
    if (revealed) {
      (0, _widgetBridge.syncTriggerAccessibility)(host, false);
      return undefined;
    }
    return (0, _widgetBridge.observeHiddenTriggers)(host);
  }, [revealed]);
  (0, _react.useEffect)(function () {
    return function () {
      var _cancelRetryRef$curre;
      return (_cancelRetryRef$curre = cancelRetryRef.current) === null || _cancelRetryRef$curre === void 0 ? void 0 : _cancelRetryRef$curre.call(cancelRetryRef);
    };
  }, []);

  // The widget's mount: its key, whether the last attempt failed, and the press waiting on it.
  var _useState3 = (0, _react.useState)(0),
    _useState4 = (0, _slicedToArray2["default"])(_useState3, 2),
    mountKey = _useState4[0],
    setMountKey = _useState4[1];
  var _useState5 = (0, _react.useState)(false),
    _useState6 = (0, _slicedToArray2["default"])(_useState5, 2),
    mountExhausted = _useState6[0],
    setMountExhausted = _useState6[1];
  var mountFailedRef = (0, _react.useRef)(false);
  var retriesLeftRef = (0, _react.useRef)(0);
  var openOnMountRef = (0, _react.useRef)(false);

  /** Start a bounded round of remounts, opening the sign-in when one lands. */
  var retryMount = (0, _react.useCallback)(function () {
    var _cancelRetryRef$curre2;
    (_cancelRetryRef$curre2 = cancelRetryRef.current) === null || _cancelRetryRef$curre2 === void 0 || _cancelRetryRef$curre2.call(cancelRetryRef);
    openOnMountRef.current = true;
    retriesLeftRef.current = MAX_MOUNT_RETRIES - 1;
    mountFailedRef.current = false;
    setMountExhausted(false);
    setRevealed(false);
    setMountKey(function (key) {
      return key + 1;
    });
  }, []);
  var openLogin = (0, _react.useCallback)(function () {
    var _cancelRetryRef$curre3;
    (_cancelRetryRef$curre3 = cancelRetryRef.current) === null || _cancelRetryRef$curre3 === void 0 || _cancelRetryRef$curre3.call(cancelRetryRef);
    cancelRetryRef.current = (0, _widgetBridge.openWidgetSignIn)(function () {
      return hostRef.current;
    }, {
      hostId: WIDGET_HOST_ID,
      // A reveal from an earlier click is undone: the trigger is back, so the host can hide again.
      onOpen: function onOpen() {
        return setRevealed(false);
      },
      // The mount failed while this press waited a frame: remount rather than reveal an empty host.
      onFallback: function onFallback() {
        return mountFailedRef.current ? retryMount() : setRevealed(true);
      }
    });
  }, [retryMount]);
  var onMount = (0, _react.useCallback)(function () {
    mountFailedRef.current = false;
    if (!openOnMountRef.current) return;
    openOnMountRef.current = false;
    openLogin();
  }, [openLogin]);
  var onMountError = (0, _react.useCallback)(function () {
    mountFailedRef.current = true;
    if (!openOnMountRef.current) return;
    if (retriesLeftRef.current > 0) {
      retriesLeftRef.current -= 1;
      setMountKey(function (key) {
        return key + 1;
      });
      return;
    }
    openOnMountRef.current = false;
    setMountExhausted(true);
  }, []);
  var onPress = (0, _react.useCallback)(function () {
    // A remount is under way; its `onMount` opens the sign-in.
    if (openOnMountRef.current) return;
    if (mountFailedRef.current) retryMount();else openLogin();
  }, [openLogin, retryMount]);
  return /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, /*#__PURE__*/_react["default"].createElement(_MainButton["default"], {
    onClick: onPress,
    label: t('gontijo_auth.login_to_complete_purchase'),
    isLoading: isLoading,
    "data-sentry-element": "MainButton",
    "data-sentry-source-file": "GontijoCheckoutLoginButton.tsx"
  }), mountExhausted && /*#__PURE__*/_react["default"].createElement("div", {
    className: "mt-4",
    role: "alert"
  }, /*#__PURE__*/_react["default"].createElement("p", {
    className: "text-sm text-error-strong"
  }, t('gontijo_auth.widget_unavailable')), /*#__PURE__*/_react["default"].createElement("button", {
    type: "button",
    className: "mt-3 rounded-md bg-primary-regular px-4 py-2 text-sm font-semibold text-white",
    onClick: retryMount
  }, t('gontijo_auth.retry'))), /*#__PURE__*/_react["default"].createElement("div", {
    ref: hostRef,
    className: revealed ? 'mt-4' : 'absolute h-0 w-0 overflow-hidden',
    "data-testid": "gontijo-checkout-login-host"
  }, /*#__PURE__*/_react["default"].createElement(_GontijoLoginWidget["default"], {
    id: WIDGET_HOST_ID,
    startupOnMount: false,
    mountKey: mountKey,
    onMount: onMount,
    onMountError: onMountError,
    "data-sentry-element": "GontijoLoginWidget",
    "data-sentry-source-file": "GontijoCheckoutLoginButton.tsx"
  })));
}